import { reverse } from 'named-urls';

import { useMatch } from 'react-router-dom';

import * as React from 'react';

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

import { observer } from 'mobx-react';

import { CircularProgress } from '@mui/material';

import Record from 'ui/Record/Record';

import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, EntityModules } from 'core/constants';
import { getEntityTranslation } from 'stores/EntityDetails/utils';
import { LastPublished } from 'containers/EntitiesFormSubmission/styles';
import routes from 'core/routes';
import { useNavBarHeight } from 'utils/hooks/navbar';
import { Button } from 'components/Button';

import MainHeader from 'components/MainHeader';
import { HeaderAction } from 'containers/EntitiesFormBuilder/styles';
import { StyledTab, StyledTabs } from 'components/Tabs/styles';

import { useStore } from 'utils/hooks/store';

import { formatMoment } from 'utils/formatters/timezone';

import DataError from 'containers/Error/DataError';
import TabPanel from 'components/CustomTabPanel/CustomTabPanel';

import { AssignmentRuleTypeEnum } from 'stores/AssignmentRules/AssignmentRules';

import { colors } from 'theme/colors';

import { MainContainer, LoadingContainer } from './styles';

import AssignmentRulesComponent from './components/AssignmentRulesComponent';

enum AssignmentRulesTabsEnum {
  Collaborators = 0,
  Pipelines = 1,
}

const AssignmentRules = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();

  const match = useMatch(routes.dashboard.settings.objects.details.assignmentRules.toString());
  const { moduleName } = match?.params as { moduleName: string };

  const {
    routing,
    assignmentRules: {
      error,
      edited,
      isSaving,
      isLoading,
      lastPublished,
      getAssignmentRules,
      updateAssignmentRule,
      updateAssignmentRulePipeline,
    },
    notification,
    session: { user },
  } = useStore();

  const [assignmentRulesTab, setAssignmentRulesTab] = React.useState(AssignmentRulesTabsEnum.Collaborators);

  React.useEffect(() => {
    const fetchAssignmentRules = async (): Promise<void> => {
      await getAssignmentRules();
    };

    fetchAssignmentRules();
  }, []);

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress data-testid="loading-progress" />
      </LoadingContainer>
    );
  }

  if (error) return <DataError />;

  const onCancel = (): void => {
    routing.push(reverse(routes.dashboard.settings.objects.details.fieldManager, { moduleName }));
  };

  const onSave = async (): Promise<void> => {
    const isAssignmentRuleUpdated = await updateAssignmentRule();
    const isAssignmentRulePipelineUpdated = await updateAssignmentRulePipeline();
    if (isAssignmentRuleUpdated && isAssignmentRulePipelineUpdated) {
      notification.enqueueSuccessSnackbar('Assignment rules updated successfully');
    } else if (!isAssignmentRuleUpdated && !isAssignmentRulePipelineUpdated) {
      notification.enqueueErrorSnackbar(
        'Rules could not be saved. Please review the highlighted errors in both tabs. No changes have been saved'
      );
    } else if (!isAssignmentRuleUpdated || !isAssignmentRulePipelineUpdated) {
      notification.enqueueErrorSnackbar(
        `Some rules could not be saved. Please review the highlighted errors in the ${
          !isAssignmentRuleUpdated ? 'Collaborators' : 'Pipelines'
        } tab. No changes have been saved.`
      );
    }
  };

  const redirectToHelpCenter = (): void => {
    window.open(user?.helpCenterUrls.assignmentRulesUrl, '_blank');
  };

  const handleChange = (_: React.SyntheticEvent, newValue: number): void => {
    setAssignmentRulesTab(newValue);
  };

  return (
    <Record
      Header={
        <MainHeader
          title="Assignment Rules"
          breadcrumbs={[
            {
              label: 'Objects',
              link: '',
            },
            {
              label: getEntityTranslation[moduleName as EntityModules].plural,
              link: reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), { moduleName }),
            },
            {
              label: 'Assignment Rules',
              link: reverse(routes.dashboard.settings.objects.details.assignmentRules, { moduleName }),
            },
          ]}
          action={
            <HeaderAction>
              {isLoading ? (
                <LoadingContainer>
                  <CircularProgress size="2rem" sx={{ color: '#9E9E9E' }} />
                  <p>Saving changes...</p>
                </LoadingContainer>
              ) : (
                lastPublished !== null && (
                  <LastPublished data-testid="last-published">
                    {`Last published: ${formatMoment(lastPublished, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}`}
                  </LastPublished>
                )
              )}

              <Button onClick={onCancel} variant="outlined" size="large">
                Cancel
              </Button>
              <Button onClick={onSave} variant="contained" size="large" disabled={!edited || isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </HeaderAction>
          }
        />
      }
      Content={() => (
        <>
          <Button
            variant="text"
            size="large"
            onClick={redirectToHelpCenter}
            endIcon={<HelpOutlineOutlinedIcon />}
            style={{
              top: '50%',
              right: '0px',
              position: 'fixed',
              marginRight: '4px',
              background: colors.tint.primary.light,
              transform: 'translateY(-50%) translateX(20px) rotate(-90deg)',
            }}
          >
            Help Centre
          </Button>
          <MainContainer>
            <StyledTabs value={assignmentRulesTab} onChange={handleChange}>
              <StyledTab label="Collaborators" id="collaborators-tab" value={0} />
              <StyledTab label="Pipelines" id="pipelines-tab" value={1} />
            </StyledTabs>
            <TabPanel value={assignmentRulesTab} index={0}>
              <AssignmentRulesComponent typeOfAssignmentRule={AssignmentRuleTypeEnum.Collaborators} />
            </TabPanel>

            <TabPanel value={assignmentRulesTab} index={1}>
              <AssignmentRulesComponent typeOfAssignmentRule={AssignmentRuleTypeEnum.Pipeline} />
            </TabPanel>
          </MainContainer>
        </>
      )}
      navBarHeight={navBarHeight}
    />
  );
};

export default observer(AssignmentRules);
